import React, { FC, useCallback, useRef } from "react";
import { useModal } from "@/hooks/useVisibility";

type Resolver = (value?: any) => void;

type DefaultModalProps = Record<string, any>;

export type ConfirmationModalProps<
    ConfirmationValue = boolean,
    ModalProps extends DefaultModalProps = DefaultModalProps,
> = Omit<ModalProps, "isOpen" | "onClose" | "onContinue"> & {
    isOpen: boolean;
    onClose: () => void;
    onContinue: (value?: ConfirmationValue) => void;
};

export const useConfirmationModal = <
    ConfirmationValue = boolean,
    ModalProps extends DefaultModalProps = DefaultModalProps,
>(
    Modal: FC<ConfirmationModalProps<ConfirmationValue, ModalProps>>,
    shouldConfirm = true,
) => {
    const { isVisible, show, hide } = useModal();
    const resolveRef = useRef<Resolver | null>(null);

    const handleContinue = (value?: ConfirmationValue) => {
        hide();
        const confirmationValue = typeof value === undefined ? true : value;
        resolveRef.current?.(confirmationValue);
    };

    const handleClose = () => {
        hide();
        resolveRef.current?.(false);
    };

    const confirm = async (): Promise<ConfirmationValue> => {
        show();
        return new Promise((resolve) => {
            resolveRef.current = resolve;
        });
    };

    /** A confirmation middleware, which fires the callback only if user continues the confirmation modal  */
    const withConfirmation = (callback: CallableFunction) => {
        return async (...arguments_: any) => {
            if (!shouldConfirm) {
                callback(...arguments_);
                return;
            }

            const confirmation = await confirm();

            if (confirmation) {
                callback(...arguments_);
            }
        };
    };

    const ConfirmationModal = useCallback(
        (props: ModalProps) => {
            return <Modal isOpen={isVisible} onClose={handleClose} onContinue={handleContinue} {...props} />;
        },
        /* eslint-disable-next-line react-hooks/exhaustive-deps -- Only re-render modal when visibility is changed */
        [isVisible],
    );

    return { ConfirmationModal, confirm, withConfirmation };
};
